import { useState } from 'react';
import SEO from '@/components/feature/SEO';
import Breadcrumb from '@/components/feature/Breadcrumb';

const testimonials = [
  {
    name: '粉丝小明',
    role: '资深粉丝 · 关注5年',
    content: '安斋拉拉主题站是我每天必逛的网站！更新及时，内容全面，社区氛围也特别好。在这里认识了很多志同道合的朋友，真心推荐给每一位安斋拉拉的粉丝。',
  },
  {
    name: '粉丝小花',
    role: '新入坑粉丝 · 关注1年',
    content: '作为新人粉丝，这个网站帮我快速了解了安斋拉拉的全部作品和资料。入门指南做得特别贴心，让我很快就融入了粉丝大家庭。',
  },
  {
    name: '粉丝大伟',
    role: '社区活跃用户 · 关注3年',
    content: '网站的社区功能做得很好，讨论氛围非常融洽。管理员团队也很负责，经常组织各种有趣的活动。希望网站越办越好！',
  },
  {
    name: '粉丝小美',
    role: '忠实粉丝 · 关注4年',
    content: '资讯更新速度很快，经常能在这里第一时间看到安斋拉拉的最新动态。作品资料库也非常齐全，查什么都很方便。五星好评！',
  },
];

const milestones = [
  { year: '2023', event: '安斋拉拉主题站正式上线，开始收录安斋拉拉相关作品与资讯。' },
  { year: '2024', event: '网站内容大幅扩充，新增资料档案与粉丝社区板块，注册用户突破万人。' },
  { year: '2025', event: '完成网站全面升级，优化浏览体验，新增深度访谈与精选图集栏目。' },
  { year: '2026', event: '持续更新优质内容，用户量突破5万，成为安斋拉拉粉丝首选信息平台。' },
];

export default function AboutPage() {
  const [formState, setFormState] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormState('success');
      return;
    }

    setFormState('submitting');
    setFormError('');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9q29rvnahb7382ov4u0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(
          Array.from(formData.entries())
            .filter(([key]) => key !== 'phone_alt')
            .map(([key, value]) => [key, value.toString()])
        ).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormState('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText;
        setFormState('error');
        setFormError(serverMsg || '提交失败，请稍后重试');
      }
    } catch {
      setFormState('error');
      setFormError('网络错误，请检查连接后重试');
    }
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '安斋拉拉主题站',
    url: 'https://anzai-rara.com',
    description: '安斋拉拉官方主题落地页，粉丝信息聚合平台。',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      availableLanguage: ['Chinese'],
    },
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于我们 — 安斋拉拉主题站',
    url: 'https://anzai-rara.com/about',
    description: '了解安斋拉拉主题站的品牌故事、发展历程与团队理念。',
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(aboutSchema)}</script>
      <SEO
        title="关于我们 — 安斋拉拉主题站"
        description="了解安斋拉拉主题站的品牌故事、发展历程、口碑评价与联系方式。我们是最全面的安斋拉拉粉丝信息聚合平台。"
        keywords="安斋拉拉,关于我们,安斋拉拉主题站,联系我们,粉丝平台"
        canonicalUrl="https://anzai-rara.com/about"
      />

      <div className="relative w-full h-[250px] md:h-[320px] overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Elegant%20Japanese%20minimalist%20artistic%20header%20with%20warm%20coral%20and%20cream%20gradient%2C%20soft%20dreamy%20atmospheric%20lighting%2C%20abstract%20organic%20shapes%2C%20cinematic%20mood%2C%20sophisticated%20editorial%20aesthetic%2C%20no%20text%2C%20no%20people&width=1400&height=500&seq=about-header-01&orientation=landscape"
          alt="关于我们"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/20 to-black/50"></div>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center px-4">
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-background-50 mb-3">
              关于我们
            </h1>
            <p className="text-background-50/70 text-sm md:text-base">
              了解我们的故事、愿景与团队
            </p>
          </div>
        </div>
      </div>

      <Breadcrumb
        items={[
          { name: '首页', path: '/' },
          { name: '关于我们' },
        ]}
      />

      <section className="w-full py-16 md:py-20 bg-background-50">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
              品牌故事
            </h2>
            <p className="text-foreground-500 leading-relaxed max-w-2xl mx-auto">
              安斋拉拉主题站创立于2023年，是全网最全面的安斋拉拉粉丝信息聚合平台。我们致力于为全球安斋拉拉粉丝提供最新、最全、最优质的内容服务。
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
            <div className="p-6 bg-background-100 rounded-lg text-center">
              <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-primary-100 flex items-center justify-center">
                <i className="ri-eye-line text-2xl text-primary-600"></i>
              </div>
              <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">我们的愿景</h3>
              <p className="text-sm text-foreground-500 leading-relaxed">
                成为全球安斋拉拉粉丝最信赖的一站式信息平台，传播优质内容，连接每一位热爱她的人。
              </p>
            </div>
            <div className="p-6 bg-background-100 rounded-lg text-center">
              <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-accent-100 flex items-center justify-center">
                <i className="ri-star-line text-2xl text-accent-600"></i>
              </div>
              <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">我们的使命</h3>
              <p className="text-sm text-foreground-500 leading-relaxed">
                持续追踪、整理和分享安斋拉拉相关资讯，让每一位粉丝都能轻松获取最准确、最及时的信息。
              </p>
            </div>
            <div className="p-6 bg-background-100 rounded-lg text-center">
              <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-secondary-100 flex items-center justify-center">
                <i className="ri-heart-line text-2xl text-secondary-600"></i>
              </div>
              <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">我们的价值观</h3>
              <p className="text-sm text-foreground-500 leading-relaxed">
                以粉丝为中心，坚持内容品质与社区温度并重，营造积极健康、包容友善的交流环境。
              </p>
            </div>
          </div>

          <div className="mb-16">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-8 text-center">
              发展历程
            </h2>
            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-background-200 -translate-x-1/2 hidden md:block"></div>
              <div className="space-y-8">
                {milestones.map((m, index) => (
                  <div key={m.year} className={`flex flex-col md:flex-row gap-4 md:gap-8 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                    <div className="md:w-1/2 flex md:justify-end">
                      <div className={`bg-background-100 rounded-lg p-5 ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                        <span className="inline-block px-3 py-1 bg-primary-500 text-background-50 text-xs rounded-full font-medium mb-2">
                          {m.year}
                        </span>
                        <p className="text-sm text-foreground-600 leading-relaxed">{m.event}</p>
                      </div>
                    </div>
                    <div className="hidden md:flex w-8 items-center justify-center">
                      <div className="w-3 h-3 rounded-full bg-primary-500"></div>
                    </div>
                    <div className="md:w-1/2"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="mb-16">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-8 text-center">
              粉丝口碑
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {testimonials.map((t, i) => (
                <div key={i} className="p-5 bg-background-100 rounded-lg">
                  <div className="flex items-center gap-1 mb-3">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <i key={star} className="ri-star-fill text-accent-500 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    "{t.content}"
                  </p>
                  <div>
                    <p className="text-sm font-semibold text-foreground-900">{t.name}</p>
                    <p className="text-xs text-foreground-400">{t.role}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-8 text-center">
              联系我们
            </h2>
            <div className="max-w-lg mx-auto">
              {formState === 'success' ? (
                <div className="text-center py-12 bg-background-100 rounded-lg">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
                    <i className="ri-check-line text-3xl text-green-600"></i>
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">提交成功！</h3>
                  <p className="text-sm text-foreground-500">感谢你的留言，我们会尽快回复。</p>
                  <button
                    className="mt-6 px-6 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                    onClick={() => setFormState('idle')}
                  >
                    继续留言
                  </button>
                </div>
              ) : (
                <form
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="space-y-5"
                >
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      姓名 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-4 py-3 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入你的姓名"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      邮箱 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-4 py-3 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入你的邮箱"
                    />
                  </div>

                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      主题
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      className="w-full px-4 py-3 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                      placeholder="留言主题（选填）"
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      留言内容 <span className="text-primary-500">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      maxLength={500}
                      rows={5}
                      className="w-full px-4 py-3 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                      placeholder="请输入你的留言内容（500字以内）"
                    ></textarea>
                  </div>

                  <div className="absolute opacity-0 pointer-events-none" aria-hidden="true">
                    <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" readOnly />
                  </div>

                  {formState === 'error' && formError && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                      {formError}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={formState === 'submitting'}
                    className="w-full px-6 py-3 bg-primary-500 text-background-50 rounded-full font-medium text-sm hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-300 whitespace-nowrap cursor-pointer"
                  >
                    {formState === 'submitting' ? '提交中...' : '提交留言'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}